import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { FileText, Upload, Download, ArrowRight } from 'lucide-react';
import api from '../services/api';

export const ProfessionalDocumentsPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['professional-documents-page'],
    queryFn: async () => {
      try {
        const [screeningResponse, contractResponse] = await Promise.all([
          api.get('/screening/status'),
          api.get('/professional/contract/status').catch(() => ({ data: { data: {} } }))
        ]);
        const screening = screeningResponse.data?.data?.screening;
        const contractSigned = contractResponse.data?.data?.contract_signed;
        const onboardingStep = screeningResponse.data?.data?.onboarding_step;
        const shouldStartWizard = screeningResponse.data?.data?.should_start_wizard;

        const docs: Array<{
          id: string;
          name: string;
          type: string;
          uploaded_at: string;
          url?: string;
        }> = [];

        if (screening) {
          if (screening.kvk_uittreksel_url) {
            docs.push({
              id: 'kvk',
              name: 'KVK Uittreksel',
              type: 'KVK',
              uploaded_at: screening.kvk_uittreksel_upload_date || screening.created_at,
              url: screening.kvk_uittreksel_url
            });
          }
          if (screening.policy_document_url) {
            docs.push({
              id: 'insurance',
              name: 'Verzekeringspolis',
              type: 'Verzekering',
              uploaded_at: screening.created_at,
              url: screening.policy_document_url
            });
          }
        }

        if (contractSigned) {
          docs.push({
            id: 'contract',
            name: 'BeLagom Contract',
            type: 'Contract',
            uploaded_at: contractResponse.data.data.signed_at
          });
        }

        return { documents: docs, screening, onboardingStep, shouldStartWizard };
      } catch {
        return { documents: [], screening: null, onboardingStep: null, shouldStartWizard: false };
      }
    }
  });

  const { documents = [], screening, shouldStartWizard } = documentsData || {};

  const getDocumentUrl = (url: string) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) return url;
    return `/${url.replace(/^\/+/, '')}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('profDocuments.title')}</h1>
        <p className="mt-1 text-gray-600">{t('profDocuments.subtitle')}</p>
      </div>

      {!screening && shouldStartWizard && (
        <Card className="mb-8 border-2 border-green-200 bg-green-50">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('profDocuments.uploadTitle')}</h2>
            <p className="text-gray-600 mb-4">{t('profDocuments.uploadDesc')}</p>
            <Button onClick={() => navigate('/professional/screening')}>
              <Upload className="w-4 h-4 mr-2" />
              {t('profDocuments.startScreening')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Card>
      )}

      {screening?.status === 'pending' && (
        <Card className="mb-8 border-2 border-yellow-200 bg-yellow-50">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('profDocuments.pendingTitle')}</h2>
            <p className="text-gray-600 mb-4">{t('profDocuments.pendingDesc')}</p>
            <Button variant="outline" onClick={() => navigate('/professional/screening/pending')}>
              {t('profDocuments.viewStatus')}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('profDocuments.overviewTitle')}</h2>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('profDocuments.loading')}</p>
            </div>
          ) : documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{doc.name}</h3>
                      {doc.type && <p className="text-xs text-gray-500 mt-1">{doc.type}</p>}
                      {doc.uploaded_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          {t('profDocuments.uploaded')} {new Date(doc.uploaded_at).toLocaleDateString('nl-NL')}
                        </p>
                      )}
                      {doc.url && (
                        <a
                          href={getDocumentUrl(doc.url) || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center mt-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {t('profDocuments.download')}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('profDocuments.noDocsTitle')}</h3>
              <p className="text-gray-600 mb-4">
                {!screening ? t('profDocuments.noDocsDesc') : t('profDocuments.noDocsDesc2')}
              </p>
              {!screening && (
                <Button onClick={() => navigate('/professional/screening')}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('profDocuments.uploadViaScreening')}
                </Button>
              )}
              {screening && screening.status === 'rejected' && (
                <Button onClick={() => navigate('/professional/screening')}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('profDocuments.resubmit')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <p className="mt-4 text-sm text-gray-500">{t('profDocuments.renewHint')}</p>
    </div>
  );
};
